import React, { useState, useEffect, useContext } from 'react';
import { shape, string, array, func } from 'prop-types';
import InputSelector from './ProFlowInputSelector';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Section } from 'wg-fe-ui';
import { ProFlowStorageContext } from '../services/context/ProFlowStorageContext';

const ProFlowQuestionsPolicyHolderSection = ({
  taggedQuestions,
  allQuestions,
  questionErrors,
  tag,
  focus,
  setFocus,
}) => {
  const [questionList, setQuestionList] = useState(taggedQuestions);
  const { answers } = useContext(ProFlowStorageContext);
  const { i18n } = useTranslation();

  const TYPE_OF_POLICYHOLDER_ID = '625c4823-4374-4343-af33-ec54c0a40d88';

  const fullWidthSpans = [
    'e1ff7109-0552-475f-b4bc-4b8ab3920d67', // Address
  ];
  const thirdWidthSpans = [];
  const halfSpanLonely = [
    'a0db7b5a-6d00-45f9-9040-db56065652f8', // Gender
    'e1ff7109-0552-475f-b4bc-4b8ab3930d72', // Teacher checkmark
  ];

  useEffect(() => {
    filterRequiredAnswers();
  }, [answers]);

  function filterRequiredAnswers() {
    let toBeShowedQuestions = [];
    toBeShowedQuestions = taggedQuestions.filter(question => {
      const { required } = question;
      return showCriteria(required, answers);
    });
    setQuestionList(toBeShowedQuestions);
  }

  function showCriteria(required = [], _answers = []) {
    // show input if nothing is required
    if (required.length === 0) return true;
    const nulledRequires = required.filter(({ answer }) => answer === null);
    const generalRequires = required.filter(({ answer }) => answer !== null);

    const shouldShowQuestion =
      areAllNulledQuestionsAnswered(nulledRequires, answers) ||
      isOneOfQeneralQuestionsAnswered(generalRequires, answers);

    return shouldShowQuestion;
  }

  function areAllNulledQuestionsAnswered(required = [], _answers = []) {
    // if there are no nulled questions return true
    if (required.length === 0) return false;

    // return true if all nulled questions are answered
    return required
      .map(({ id }) => _answers.some(({ id: _id }) => id === _id))
      .includes(false);
  }

  function isOneOfQeneralQuestionsAnswered(required = [], _answers = []) {
    // if there are no questions return true
    if (required.length === 0) return false;
    // return true if id and answer matches
    return required.some(require =>
      _answers.some(
        ({ id, answer }) => require.id === id && answer === require.answer,
      ),
    );
  }

  function getSpanLenght(id) {
    if (fullWidthSpans.includes(id) || halfSpanLonely.includes(id)) return 6;
    if (thirdWidthSpans.includes(id)) return 2;
    return 3;
  }

  function getElementwidth(id) {
    if (halfSpanLonely.includes(id)) return 'calc(50% - 10px);';
  }
  // console.log({ questionList });

  return (
    questionList?.length > 0 && (
      <>
        {questionList
          .filter(({ id }) => TYPE_OF_POLICYHOLDER_ID === id)
          .map(
            ({
              id,
              rules,
              error_messages,
              title,
              insurance_type_tags,
              tag,
              field,
              validator,
            }) => (
              <>
                <Title>{i18n.t('Type of policyholder')}</Title>
                <StyledInputSelector
                  typeTags={insurance_type_tags}
                  key={id}
                  validator={validator}
                  questionErrors={questionErrors}
                  rules={rules}
                  name={id}
                  tag={tag}
                  field={field}
                  errorMessages={error_messages}
                  title={title?.[i18n.language]}
                  focus={focus}
                  setFocus={setFocus}
                />
              </>
            ),
          )}
        <StyledTitle data-test-id="policy-holder-title">
          {i18n.t(`Information from the ${tag}`)}
        </StyledTitle>
        <SubTitle>{i18n.t('Please fill in your personal data')}</SubTitle>
        <StyledSection background>
          {questionList
            .filter(({ id }) => TYPE_OF_POLICYHOLDER_ID !== id)
            .map(
              ({
                id,
                rules,
                error_messages,
                title,
                insurance_type_tags,
                field,
                validator,
                tag,
              }) => (
                <StyledDiv
                  key={id}
                  span={getSpanLenght(id)}
                  width={getElementwidth(id)}
                >
                  <InputSelector
                    validator={validator}
                    typeTags={insurance_type_tags}
                    key={id}
                    questionErrors={questionErrors}
                    rules={rules}
                    field={field}
                    name={id}
                    errorMessages={error_messages}
                    title={title?.[i18n.language]}
                    tag={tag}
                    focus={focus}
                    setFocus={setFocus}
                  />
                </StyledDiv>
              ),
            )}
        </StyledSection>
      </>
    )
  );
};

const StyledDiv = styled.div`
  grid-column: span ${({ span }) => span};
  width: ${({ width }) => width};
  h3 {
    color: #8990a3;
    font-weight: 600;
    letter-spacing: 0.01em;
    font-size: 14px;
  }
`;

const StyledInputSelector = styled(InputSelector)`
  margin-bottom: 4rem;

  div {
    box-shadow: none;
  }
`;

const StyledSection = styled(Section.Content)`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  column-gap: 36px;
  /* padding-bottom: 0; */
`;

const Title = styled.h2`
  font-size: 2.1rem;
  color: #0e0e0e;
  font-weight: 700;
  margin: 1rem 0;
  margin-top: 3rem;
`;

const StyledTitle = styled.div`
  font-size: 2.1rem;
  color: #0e0e0e;
  font-weight: 700;
  margin: 4rem 0 1rem;
`;

const SubTitle = styled.h3`
  font-size: 1.6rem;
  color: #8990a3;
  font-weight: 500;
`;

ProFlowQuestionsPolicyHolderSection.propTypes = {
  taggedQuestions: shape().isRequired,
  allQuestions: shape().isRequired,
  questionErrors: array,
  tag: string,
  focus: string,
  setFocus: func,
};
export default ProFlowQuestionsPolicyHolderSection;
